import React, { useState, useEffect, useRef } from 'react';
import { Swords, Zap, Clock, Trophy, Users, Gift, Target, Shield, Flame, History, Check, X, Sword, Heart, Zap as ZapIcon, Crown } from 'lucide-react';
import { cn } from '../lib/utils';

interface MatchRecord {
  id: number;
  opponent: string;
  result: 'win' | 'loss';
  date: string;
  prize: number;
  creditsUsed: number;
}

interface LeaderboardEntry {
  rank: number;
  name: string;
  wins: number;
  winRate: string;
}

interface BattleState {
  playerHealth: number;
  opponentHealth: number;
  playerAction: string;
  opponentAction: string;
  turn: number;
  battleLog: string[];
  status: 'matching' | ' battling' | 'ended';
  winner: 'player' | 'opponent' | null;
}

const mockRecentMatches: MatchRecord[] = [
  { id: 1, opponent: 'CyberNinja_99', result: 'win', date: '2 min ago', prize: 150, creditsUsed: 18 },
  { id: 2, opponent: 'BattleBot_X', result: 'loss', date: '15 min ago', prize: 0, creditsUsed: 12 },
  { id: 3, opponent: 'AI_Destroyer', result: 'win', date: '1 hour ago', prize: 150, creditsUsed: 22 },
];

const mockLeaderboard: LeaderboardEntry[] = [
  { rank: 1, name: 'ClawMaster', wins: 156, winRate: '78%' },
  { rank: 2, name: 'PVP_King', wins: 142, winRate: '71%' },
  { rank: 3, name: 'QuickDraw', wins: 128, winRate: '68%' },
  { rank: 4, name: 'ShadowHunter', wins: 115, winRate: '65%' },
  { rank: 5, name: 'NeuralNinja', wins: 98, winRate: '62%' },
];

const OPPONENT_NAMES = ['CyberNinja', 'BattleBot', 'AI_Destroyer', 'ShadowHunter', 'NeuralNinja', 'QuantumAI', 'SteelClaw', 'VoidWalker'];

const ACTION_TYPES = [
  { name: 'Attack', damage: [15, 25], icon: Sword, color: 'text-red-500' },
  { name: 'Heavy Strike', damage: [25, 35], icon: Sword, color: 'text-red-600' },
  { name: 'Defend', damage: [0, 5], icon: Shield, color: 'text-blue-500' },
  { name: 'Quick Strike', damage: [10, 18], icon: ZapIcon, color: 'text-yellow-500' },
  { name: 'Counter', damage: [12, 20], icon: Target, color: 'text-purple-500' },
];

const PVP = () => {
  const [isMatching, setIsMatching] = useState(false);
  const [activeTab, setActiveTab] = useState<'play' | 'history' | 'leaderboard'>('play');
  const [selectedStrategy, setSelectedStrategy] = useState<'aggressive' | 'balanced' | 'defensive'>('balanced');
  const [battleState, setBattleState] = useState<BattleState | null>(null);
  const [opponentName, setOpponentName] = useState('');
  const battleTimerRef = useRef<NodeJS.Timeout | null>(null);

  const strategies = [
    { id: 'aggressive', name: 'Aggressive', icon: Flame, desc: 'High risk, high reward', color: 'text-red-500' },
    { id: 'balanced', name: 'Balanced', icon: Target, desc: 'Adaptive tactics', color: 'text-gold' },
    { id: 'defensive', name: 'Defensive', icon: Shield, desc: 'Survival focused', color: 'text-blue-500' },
  ];

  const handleQuickMatch = () => {
    setIsMatching(true);
    
    // Simulate finding opponent
    setTimeout(() => {
      const randomOpponent = OPPONENT_NAMES[Math.floor(Math.random() * OPPONENT_NAMES.length)] + '_' + Math.floor(Math.random() * 100);
      setOpponentName(randomOpponent);
      setIsMatching(false);
      startBattle(randomOpponent);
    }, 2000);
  };

  const startBattle = (opponent: string) => {
    const initialState: BattleState = {
      playerHealth: 100,
      opponentHealth: 100,
      playerAction: '',
      opponentAction: '',
      turn: 0,
      battleLog: [`Match found! You vs ${opponent}`, 'Battle starting...'],
      status: ' battling',
      winner: null,
    };
    setBattleState(initialState);

    // Start battle simulation
    let turn = 0;
    const maxTurns = 15;
    
    const battleInterval = setInterval(() => {
      turn++;
      
      setBattleState(prev => {
        if (!prev || prev.status === 'ended') return prev;
        
        // Generate actions
        const playerAction = ACTION_TYPES[Math.floor(Math.random() * ACTION_TYPES.length)];
        const opponentAction = ACTION_TYPES[Math.floor(Math.random() * ACTION_TYPES.length)];
        
        // Calculate damage
        let playerDamage = 0;
        let opponentDamage = 0;
        
        if (playerAction.name !== 'Defend') {
          playerDamage = Math.floor(Math.random() * (playerAction.damage[1] - playerAction.damage[0]) + playerAction.damage[0]);
        }
        if (opponentAction.name !== 'Defend') {
          opponentDamage = Math.floor(Math.random() * (opponentAction.damage[1] - opponentAction.damage[0]) + opponentAction.damage[0]);
        }
        
        // Apply strategy modifiers
        if (selectedStrategy === 'aggressive') {
          playerDamage = Math.floor(playerDamage * 1.2);
        } else if (selectedStrategy === 'defensive') {
          opponentDamage = Math.floor(opponentDamage * 0.8);
        }
        
        const newPlayerHealth = Math.max(0, prev.playerHealth - opponentDamage);
        const newOpponentHealth = Math.max(0, prev.opponentHealth - playerDamage);
        
        const newLog = [...prev.battleLog];
        newLog.push(`Turn ${turn}: You used ${playerAction.name} (-${playerDamage} HP), ${opponent} used ${opponentAction.name} (-${opponentDamage} HP)`);
        
        // Check win condition
        let winner: 'player' | 'opponent' | null = null;
        let status: 'matching' | ' battling' | 'ended' = ' battling';
        
        if (newOpponentHealth <= 0) {
          winner = 'player';
          status = 'ended';
          newLog.push(`Victory! You defeated ${opponent}!`);
        } else if (newPlayerHealth <= 0) {
          winner = 'opponent';
          status = 'ended';
          newLog.push(`Defeat! ${opponent} defeated you.`);
        } else if (turn >= maxTurns) {
          // Draw - higher health wins
          if (newPlayerHealth > newOpponentHealth) {
            winner = 'player';
            newLog.push(`Victory by points!`);
          } else {
            winner = 'opponent';
            newLog.push(`Defeat by points.`);
          }
          status = 'ended';
        }
        
        return {
          ...prev,
          playerHealth: newPlayerHealth,
          opponentHealth: newOpponentHealth,
          playerAction: playerAction.name,
          opponentAction: opponentAction.name,
          turn,
          battleLog: newLog.slice(-6), // Keep last 6 messages
          status,
          winner,
        };
      });
      
      if (turn >= maxTurns) {
        clearInterval(battleInterval);
      }
    }, 1500);
    
    battleTimerRef.current = battleInterval;
  };

  const closeBattle = () => {
    if (battleTimerRef.current) {
      clearInterval(battleTimerRef.current);
    }
    setBattleState(null);
    setOpponentName('');
  };

  useEffect(() => {
    return () => {
      if (battleTimerRef.current) {
        clearInterval(battleTimerRef.current);
      }
    };
  }, []);

  return (
    <section className="px-4 md:px-8 py-16 md:py-24">
      <div className="max-w-[1000px] mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 rounded-full mb-4">
            <Gift size={14} className="text-gold" />
            <span className="text-xs font-medium text-gold">Free to Play</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-4">
            PVP Arena
          </h1>
          <p className="text-muted">
            1v1 instant battles. Free to enter. Win platform rewards.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-4 gap-4 mb-10">
          <div className="p-4 border border-line rounded-xl text-center">
            <div className="text-2xl font-mono font-bold text-gold">1,247</div>
            <div className="text-xs text-muted">Online Now</div>
          </div>
          <div className="p-4 border border-line rounded-xl text-center">
            <div className="text-2xl font-mono font-bold text-ink">&lt;10s</div>
            <div className="text-xs text-muted">Match Time</div>
          </div>
          <div className="p-4 border border-line rounded-xl text-center">
            <div className="text-2xl font-mono font-bold text-gold">~3min</div>
            <div className="text-xs text-muted">Battle Duration</div>
          </div>
          <div className="p-4 border border-line rounded-xl text-center">
            <div className="text-2xl font-mono font-bold text-ink">100%</div>
            <div className="text-xs text-muted">Prize to Winner</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-surface border border-line rounded-full p-1">
            {[
              { id: 'play', label: 'Play', icon: Swords },
              { id: 'history', label: 'History', icon: History },
              { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2",
                  activeTab === tab.id
                    ? "bg-gold text-bg"
                    : "text-muted hover:text-ink"
                )}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Play Tab */}
        {activeTab === 'play' && (
          <div className="space-y-8">
            {/* Strategy Selection */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-center">Select Strategy</h3>
              <div className="grid grid-cols-3 gap-4">
                {strategies.map((strategy) => (
                  <button
                    key={strategy.id}
                    onClick={() => setSelectedStrategy(strategy.id as any)}
                    className={cn(
                      "p-5 border rounded-xl text-center transition-all",
                      selectedStrategy === strategy.id
                        ? "border-gold bg-gold/5"
                        : "border-line hover:border-gold/30"
                    )}
                  >
                    <strategy.icon size={24} className={cn("mx-auto mb-2", strategy.color)} />
                    <div className="font-semibold mb-1">{strategy.name}</div>
                    <div className="text-xs text-muted">{strategy.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Match Card */}
            <div className="p-8 border border-gold/30 rounded-2xl bg-gold/5 text-center">
              <Swords size={48} className="mx-auto mb-4 text-gold" />
              <h2 className="text-2xl font-bold mb-2">Quick Match</h2>
              <p className="text-muted mb-6">
                Instantly match with a random opponent. FREE to enter, only AI credits consumed.
              </p>
              
              <div className="flex justify-center gap-8 mb-6">
                <div className="text-center">
                  <div className="font-mono font-bold text-gold">1v1</div>
                  <div className="text-xs text-muted">Match</div>
                </div>
                <div className="text-center">
                  <div className="font-mono font-bold text-gold">~15-30</div>
                  <div className="text-xs text-muted">Credits Used</div>
                </div>
                <div className="text-center">
                  <div className="font-mono font-bold text-gold">100%</div>
                  <div className="text-xs text-muted">Prize to Winner</div>
                </div>
              </div>

              <button
                onClick={handleQuickMatch}
                disabled={isMatching || battleState !== null}
                className={cn(
                  "px-12 py-4 rounded-lg font-semibold text-lg transition-all",
                  isMatching || battleState !== null
                    ? "bg-muted text-ink cursor-not-allowed" 
                    : "bg-gold text-bg hover:bg-white"
                )}
              >
                {isMatching ? 'Finding Opponent...' : battleState ? 'In Battle...' : 'Start Quick Match'}
              </button>
              <p className="text-xs text-muted mt-3">
                No entry fee. Only AI credits consumed during battle.
              </p>
            </div>

            {/* How It Works */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-5 border border-line rounded-xl text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-gold/10 text-gold">
                  <Target size={20} />
                </div>
                <h3 className="font-semibold mb-1">1. Choose Strategy</h3>
                <p className="text-sm text-muted">Select your battle approach</p>
              </div>
              <div className="p-5 border border-line rounded-xl text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-gold/10 text-gold">
                  <Zap size={20} />
                </div>
                <h3 className="font-semibold mb-1">2. Quick Match</h3>
                <p className="text-sm text-muted">Instant opponent matching</p>
              </div>
              <div className="p-5 border border-line rounded-xl text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-gold/10 text-gold">
                  <Trophy size={20} />
                </div>
                <h3 className="font-semibold mb-1">3. Win Prize</h3>
                <p className="text-sm text-muted">Winner takes all rewards</p>
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recent Matches</h3>
              <div className="text-sm text-muted">Last 10 matches</div>
            </div>
            {mockRecentMatches.map((match) => (
              <div key={match.id} className="flex items-center justify-between p-4 border border-line rounded-xl">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    match.result === 'win' ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"
                  )}>
                    {match.result === 'win' ? <Trophy size={18} /> : <Swords size={18} />}
                  </div>
                  <div>
                    <div className="font-semibold">vs {match.opponent}</div>
                    <div className="text-xs text-muted">{match.date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <div className="text-xs text-muted">Credits</div>
                    <div className="font-mono text-sm">{match.creditsUsed}</div>
                  </div>
                  <div className="w-20">
                    <div className={cn(
                      "font-mono font-bold",
                      match.result === 'win' ? "text-green-500" : "text-muted"
                    )}>
                      {match.result === 'win' ? `+${match.prize}` : '0'} $MON
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Top PVP Players</h3>
              <div className="text-sm text-muted">This Week</div>
            </div>
            <div className="space-y-2">
              {mockLeaderboard.map((entry) => (
                <div key={entry.rank} className="flex items-center justify-between p-4 border border-line rounded-xl">
                  <div className="flex items-center gap-4">
                    <span className={cn(
                      "w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold",
                      entry.rank <= 3 ? "bg-gold/20 text-gold" : "bg-surface text-muted"
                    )}>
                      {entry.rank}
                    </span>
                    <span className="font-semibold">{entry.name}</span>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <div className="text-xs text-muted">Wins</div>
                      <div className="font-mono font-bold">{entry.wins}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted">Win Rate</div>
                      <div className="font-mono font-bold text-gold">{entry.winRate}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Revenue Info */}
        <div className="mt-10 p-6 border border-line rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Gift size={18} className="text-gold" />
            <h3 className="font-semibold">Revenue Sharing Prize Pool</h3>
          </div>
          <p className="text-sm text-muted">
            All PVP prizes are funded by platform revenue sharing. Platform revenue comes from 
            LLM token resale commissions and brand partnership service fees. Play for FREE, 
            only AI credits consumed during battles (~15-30 credits per match).
          </p>
        </div>
      </div>

      {/* Battle Modal */}
      {battleState && (
        <div className="fixed inset-0 bg-bg/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-line rounded-2xl w-full max-w-lg overflow-hidden">
            {/* Battle Header */}
            <div className="p-5 border-b border-line flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Swords size={24} className="text-gold" />
                <div>
                  <h2 className="text-lg font-bold">PVP Battle</h2>
                  <p className="text-xs text-muted">Turn {battleState.turn}</p>
                </div>
              </div>
              {battleState.status === 'ended' && (
                <button onClick={closeBattle} className="text-muted hover:text-ink">
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Battle Arena */}
            <div className="p-6">
              {/* Health Bars */}
              <div className="flex items-center justify-between gap-6 mb-6">
                {/* Player */}
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gold/10 flex items-center justify-center">
                    <Crown size={32} className="text-gold" />
                  </div>
                  <div className="font-semibold mb-2">You</div>
                  <div className="h-4 bg-bg rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all duration-500"
                      style={{ width: `${battleState.playerHealth}%` }}
                    />
                  </div>
                  <div className="text-sm font-mono mt-1">{battleState.playerHealth} HP</div>
                  {battleState.playerAction && (
                    <div className="text-xs text-gold mt-1">{battleState.playerAction}</div>
                  )}
                </div>

                {/* VS */}
                <div className="text-2xl font-bold text-muted">VS</div>

                {/* Opponent */}
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-red-500/10 flex items-center justify-center">
                    <Swords size={32} className="text-red-500" />
                  </div>
                  <div className="font-semibold mb-2 truncate">{opponentName}</div>
                  <div className="h-4 bg-bg rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-500 transition-all duration-500"
                      style={{ width: `${battleState.opponentHealth}%` }}
                    />
                  </div>
                  <div className="text-sm font-mono mt-1">{battleState.opponentHealth} HP</div>
                  {battleState.opponentAction && (
                    <div className="text-xs text-red-400 mt-1">{battleState.opponentAction}</div>
                  )}
                </div>
              </div>

              {/* Battle Log */}
              <div className="bg-bg rounded-xl p-4 h-40 overflow-y-auto mb-4">
                {battleState.battleLog.map((log, index) => (
                  <div key={index} className="text-sm mb-1 last:mb-0">
                    {log.includes('Victory') ? (
                      <span className="text-green-500 font-semibold">{log}</span>
                    ) : log.includes('Defeat') ? (
                      <span className="text-red-500 font-semibold">{log}</span>
                    ) : (
                      <span className="text-muted">{log}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Battle Result */}
              {battleState.status === 'ended' && battleState.winner && (
                <div className="text-center">
                  {battleState.winner === 'player' ? (
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                      <Trophy size={32} className="mx-auto mb-2 text-green-500" />
                      <div className="text-xl font-bold text-green-500 mb-1">Victory!</div>
                      <div className="text-sm text-muted">You won 150 $MON</div>
                    </div>
                  ) : (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                      <X size={32} className="mx-auto mb-2 text-red-500" />
                      <div className="text-xl font-bold text-red-500 mb-1">Defeat</div>
                      <div className="text-sm text-muted">Better luck next time!</div>
                    </div>
                  )}
                  <button
                    onClick={closeBattle}
                    className="mt-4 px-8 py-3 bg-gold text-bg rounded-lg font-semibold hover:bg-white transition-colors"
                  >
                    Continue
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PVP;
